// ─────────────────────────────────────────────────────────────
// scrapReport.config.js — Report 7: scrap sale report
// Shape: statement (portrait) — month-wise, buyer-wise, entries
// Data:  { months, buyers, totals } from
//        /tape/finance/scrap-sales/report
//        plus the raw sale rows for the detail section
//
// Scrap carries no cost — the whole input cost of a jumbo loads
// onto its good cores. So every figure here is gross income
// with nothing to net against it.
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export const scrapReportConfig = ({ report, sales, filters, buyers }) => {
  const t = report.totals || {};
  const totalAmount = num(t.total_amount);
  const totalKg = num(t.total_kg);

  const selected = (buyers || []).find(
    (b) => String(b.id) === String(filters.buyer_id)
  );

  const monthRows = (report.months || []).map((m) => ({
    month: m.month_label,
    sale_count: m.sale_count,
    total_kg: m.total_kg,
    avg_rate: m.avg_rate,
    total_amount: m.total_amount,
  }));

  const buyerRows = (report.buyers || []).map((b) => ({
    buyer: b.buyer_name,
    sale_count: b.sale_count,
    total_kg: b.total_kg,
    avg_rate: b.avg_rate,
    total_amount: b.total_amount,
    last_sale: b.last_sale_date,
  }));

  const saleRows = (sales || []).map((s) => ({
    date: s.sale_date,
    buyer: s.buyer_name,
    weight_kg: s.weight_kg,
    rate_per_kg: s.rate_per_kg,
    receipt_no: s.receipt_no,
    amount: s.amount,
  }));

  const sections = [
    {
      title: 'Month by Month',
      tone: 'income',
      columns: [
        { key: 'month', label: 'Month', align: 'left', fmt: 'text' },
        { key: 'sale_count', label: 'Sales', align: 'right', fmt: 'int', width: 20 },
        { key: 'total_kg', label: 'Weight', align: 'right', fmt: 'kg', width: 26 },
        { key: 'avg_rate', label: 'Avg Rate', align: 'right', fmt: 'money2', width: 24 },
        { key: 'total_amount', label: 'Value', align: 'right', fmt: 'money', width: 30, bold: true },
      ],
      rows: monthRows,
      footRow: [
        'TOTAL',
        FORMATTERS.int(t.sale_count),
        FORMATTERS.kg(totalKg),
        FORMATTERS.money2(t.avg_rate),
        FORMATTERS.money(totalAmount),
      ],
    },
  ];

  // The buyer breakdown only says something when looking at
  // everyone — filtered to one buyer it just repeats the totals.
  if (!filters.buyer_id && buyerRows.length > 0) {
    sections.push({
      title: 'By Buyer',
      tone: 'income',
      columns: [
        { key: 'buyer', label: 'Buyer', align: 'left', fmt: 'text' },
        { key: 'sale_count', label: 'Sales', align: 'right', fmt: 'int', width: 20 },
        { key: 'total_kg', label: 'Weight', align: 'right', fmt: 'kg', width: 26 },
        { key: 'avg_rate', label: 'Avg Rate', align: 'right', fmt: 'money2', width: 24 },
        { key: 'last_sale', label: 'Last Sale', align: 'left', fmt: 'date', width: 26 },
        { key: 'total_amount', label: 'Value', align: 'right', fmt: 'money', width: 30, bold: true },
      ],
      rows: buyerRows,
      footRow: [
        'TOTAL',
        FORMATTERS.int(t.sale_count),
        FORMATTERS.kg(totalKg),
        '',
        '',
        FORMATTERS.money(totalAmount),
      ],
    });
  }

  if (saleRows.length > 0) {
    sections.push({
      title: 'Sale Detail',
      tone: 'income',
      columns: [
        { key: 'date', label: 'Date', align: 'left', fmt: 'date', width: 24 },
        { key: 'buyer', label: 'Sold To', align: 'left', fmt: 'text' },
        { key: 'weight_kg', label: 'Weight', align: 'right', fmt: 'kg', width: 24 },
        { key: 'rate_per_kg', label: 'Rate / kg', align: 'right', fmt: 'money2', width: 24 },
        { key: 'receipt_no', label: 'Receipt', align: 'left', fmt: 'text', width: 24 },
        { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 28, bold: true },
      ],
      rows: saleRows,
      footRow: [
        'TOTAL',
        '',
        FORMATTERS.kg(totalKg),
        '',
        '',
        FORMATTERS.money(totalAmount),
      ],
    });
  }

  return {
    kind: 'statement',
    orientation: 'p',
    title: 'Scrap Sale Report',
    subtitle: `${FORMATTERS.date(filters.from_date)}  to  ${FORMATTERS.date(filters.to_date)}`,

    boxes: [
      {
        heading: 'Report Filters',
        rows: [
          { label: 'Buyer', value: selected ? selected.name : 'All Buyers' },
          { label: 'From Date', value: FORMATTERS.date(filters.from_date) },
          { label: 'To Date', value: FORMATTERS.date(filters.to_date) },
          { label: 'Months', value: monthRows.length },
        ],
      },
      {
        heading: 'Summary',
        rows: [
          {
            label: 'Sales',
            value: FORMATTERS.int(t.sale_count),
            label2: 'Buyers',
            value2: buyerRows.length,
          },
          { label: 'Weight Sold', value: FORMATTERS.kg(totalKg) },
          { label: 'Average Rate', value: `Rs ${FORMATTERS.money2(t.avg_rate)} / kg` },
          { label: 'Total Income', value: FORMATTERS.money(totalAmount) },
        ],
      },
    ],

    sections,

    totals: [
      { label: 'Weight Sold', value: FORMATTERS.kg(totalKg), fmt: 'text' },
      { label: 'Sales Count', value: t.sale_count || 0, fmt: 'int' },
      { label: 'Total Income', value: totalAmount, fmt: 'money', emphasis: true, tone: 'income' },
    ],

    signatures: SIGNATURES.report,
  };
};

export default scrapReportConfig;